import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Calendar, MapPin, Clock, Users, ExternalLink, ArrowLeft, Edit, Trash2, Crown, DollarSign, QrCode, Camera, Image, Upload, X, Globe, CreditCard, Wallet, Lock, Eye, EyeOff, Mail, UserPlus, Send, RefreshCw, Ticket } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasRsvp, setHasRsvp] = useState(false);
  const [hasTicket, setHasTicket] = useState(false);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('stripe');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const photoInputRef = useRef(null);
  
  // Invitation management state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [sendingInvites, setSendingInvites] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [showInvitations, setShowInvitations] = useState(false);
  const [invitationCode, setInvitationCode] = useState(null);
  const [invitationValid, setInvitationValid] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [inviteTab, setInviteTab] = useState('manual'); // 'manual' or 'csv'
  const csvInputRef = useRef(null);

  useEffect(() => {
    fetchEvent();
    fetchPhotos();
    fetchPaymentMethods();
    
    // Check for invitation code in URL
    const invite = searchParams.get('invite');
    if (invite) {
      setInvitationCode(invite);
      validateInvitation(invite);
    }
    
    if (user) {
      checkRsvp();
    }
  }, [id, user]);

  useEffect(() => {
    // Check for ticket purchase success
    const ticketSuccess = searchParams.get('ticket_success');
    const sessionId = searchParams.get('session_id');
    if (ticketSuccess === 'true' && sessionId) {
      checkTicketPayment(sessionId);
    }
  }, [searchParams]);

  const checkTicketPayment = async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          toast.success('Ticket purchased successfully!');
          setHasTicket(true);
          fetchEvent();
          navigate(`/events/${id}`, { replace: true });
        } else if (response.data.status === 'expired') {
          toast.error('Payment session expired. Please try again.');
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        }
      } catch (error) {
        console.error('Error checking ticket payment:', error);
      }
    };

    poll();
  };

  const fetchEvent = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/events/${id}`, { headers });
      setEvent(response.data);
      
      // Track event view for analytics
      try {
        await axios.post(`${API}/events/${id}/view`, {}, { headers });
      } catch (e) {
        // Silently fail view tracking
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || "You don't have access to this event");
      } else if (error.response?.status === 401) {
        toast.error('Please login to view this event');
      } else {
        toast.error('Event not found');
      }
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const checkRsvp = async () => {
    try {
      const response = await axios.get(`${API}/check-rsvp/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasRsvp(response.data.has_rsvp);
      setHasTicket(response.data.has_ticket);
    } catch (error) {
      console.error('Error checking RSVP:', error);
    }
  };

  const fetchPhotos = async () => {
    setPhotosLoading(true);
    try {
      const response = await axios.get(`${API}/events/${id}/photos`);
      setPhotos(response.data.photos || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setPhotosLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/events/${id}/photos`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Photo uploaded successfully!');
      fetchPhotos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error uploading photo');
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) {
        photoInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/events/${id}/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Photo deleted');
      fetchPhotos();
      setSelectedPhoto(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting photo');
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/payment-methods`);
      setPaymentMethods(response.data.methods || []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const handleRsvp = async () => {
    if (!user) {
      toast.error('Please login to RSVP');
      navigate('/login');
      return;
    }

    setRsvpLoading(true);
    try {
      if (hasRsvp) {
        await axios.delete(`${API}/rsvp/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHasRsvp(false);
        setEvent(prev => ({ ...prev, rsvp_count: prev.rsvp_count - 1 }));
        toast.success('RSVP cancelled');
      } else {
        // Include invitation code if present (for invite-only events)
        const rsvpData = { event_id: id };
        if (invitationCode) {
          rsvpData.invitation_code = invitationCode;
        }
        await axios.post(`${API}/rsvp`, rsvpData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHasRsvp(true);
        setEvent(prev => ({ ...prev, rsvp_count: prev.rsvp_count + 1 }));
        toast.success('Successfully RSVPed!');
        // Clear invitation code after successful RSVP
        if (invitationCode) {
          setInvitationCode(null);
          setInvitationValid(null);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error processing RSVP');
    } finally {
      setRsvpLoading(false);
    }
  };

  // Invitation management functions
  const validateInvitation = async (code) => {
    try {
      const response = await axios.get(`${API}/invitations/${code}/validate`);
      setInvitationValid(response.data);
    } catch (error) {
      setInvitationValid({ valid: false, error: error.response?.data?.detail || 'Invalid invitation' });
    }
  };

  const fetchInvitations = async () => {
    if (!token || !event) return;
    try {
      const response = await axios.get(`${API}/events/${id}/invitations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvitations(response.data.invitations || []);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    }
  };

  const handleSendInvitations = async () => {
    if (!inviteEmails.trim()) {
      toast.error('Please enter at least one email address');
      return;
    }

    const emails = inviteEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e);
    if (emails.length === 0) {
      toast.error('Please enter valid email addresses');
      return;
    }

    setSendingInvites(true);
    try {
      const response = await axios.post(
        `${API}/events/${id}/invitations`,
        { emails, custom_message: inviteMessage || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setInviteEmails('');
      setInviteMessage('');
      setInviteDialogOpen(false);
      fetchInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error sending invitations');
    } finally {
      setSendingInvites(false);
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      await axios.post(
        `${API}/events/${id}/invitations/resend/${invitationId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Invitation resent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error resending invitation');
    }
  };

  const handleRevokeInvitation = async (invitationId) => {
    try {
      await axios.delete(`${API}/events/${id}/invitations/${invitationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invitation revoked');
      fetchInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error revoking invitation');
    }
  };

  // Bulk actions for premium members
  const handleBulkResend = async () => {
    if (!window.confirm('Are you sure you want to resend all pending invitations?')) return;
    
    try {
      const response = await axios.post(
        `${API}/events/${id}/invitations/bulk/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error resending invitations');
    }
  };

  const handleBulkRevoke = async () => {
    if (!window.confirm('Are you sure you want to revoke ALL pending invitations? This action cannot be undone.')) return;
    
    try {
      const response = await axios.post(
        `${API}/events/${id}/invitations/bulk/revoke`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error revoking invitations');
    }
  };

  const isPremiumMember = () => {
    if (!user) return false;
    return user.attendee_membership === 'attendee_premium' || 
           user.business_membership === 'business_premium' ||
           user.membership_tier === 'attendee_premium' ||
           user.membership_tier === 'business_premium';
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setCsvFile(file);
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setUploadingCsv(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      if (inviteMessage) {
        formData.append('custom_message', inviteMessage);
      }

      const response = await axios.post(
        `${API}/events/${id}/invitations/csv`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const { created, skipped, total_in_file } = response.data;
      
      if (created > 0) {
        toast.success(`Sent ${created} invitation(s)${skipped > 0 ? ` (${skipped} skipped - already invited)` : ''}`);
      } else if (skipped > 0) {
        toast.info(`All ${skipped} emails were already invited`);
      }

      setCsvFile(null);
      setInviteMessage('');
      setInviteDialogOpen(false);
      if (csvInputRef.current) {
        csvInputRef.current.value = '';
      }
      fetchInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error uploading CSV');
    } finally {
      setUploadingCsv(false);
    }
  };

  const downloadCsvTemplate = async () => {
    try {
      const response = await axios.get(`${API}/events/${id}/invitations/template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'invitation_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Error downloading template');
    }
  };

  const handleBuyTicketClick = () => {
    if (!user) {
      toast.error('Please login to purchase a ticket');
      navigate('/login');
      return;
    }
    setPaymentDialogOpen(true);
  };

  const handleBuyTicket = async (method = 'stripe') => {
    if (!user) {
      toast.error('Please login to purchase a ticket');
      navigate('/login');
      return;
    }

    setTicketLoading(true);
    try {
      if (method === 'paypal') {
        // PayPal checkout
        const response = await axios.post(
          `${API}/checkout/paypal/create-order`,
          { event_id: id, origin_url: window.location.origin },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.approval_url) {
          window.location.href = response.data.approval_url;
        } else {
          toast.error('Failed to initiate PayPal checkout');
        }
      } else {
        // Stripe checkout (default)
        const response = await axios.post(
          `${API}/checkout/ticket`,
          { event_id: id, origin_url: window.location.origin },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.url) {
          window.location.href = response.data.url;
        } else {
          toast.error('Failed to initiate checkout');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error processing purchase');
    } finally {
      setTicketLoading(false);
      setPaymentDialogOpen(false);
    }
  };

  const handleBuyTicketOld = async () => {
    if (!user) {
      toast.error('Please login to purchase a ticket');
      navigate('/login');
      return;
    }

    setTicketLoading(true);
    try {
      const response = await axios.post(
        `${API}/checkout/ticket`,
        { event_id: id, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating checkout');
      setTicketLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event deleted successfully');
      navigate('/events');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting event');
    }
    setDeleteDialogOpen(false);
  };

  const getCategoryLabel = (value) => {
    const categories = {
      conference: 'Conference',
      workshop: 'Workshop',
      networking: 'Networking Mixer',
      webinar: 'Webinar',
      seminar: 'Seminar',
      training: 'Training Session',
      panel: 'Panel Discussion',
      other: 'Other',
    };
    return categories[value] || value;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!event) {
    return null;
  }

  const isOrganizer = user && user.id === event.organizer_id;
  const isAtCapacity = event.capacity && event.rsvp_count >= event.capacity;
  const isRegistered = hasRsvp || hasTicket;

  const getRegionLabel = (value) => {
    const regions = {
      nyc: 'New York City',
      long_island: 'Long Island',
      westchester: 'Westchester County',
      connecticut: 'Connecticut',
      new_jersey: 'New Jersey'
    };
    return regions[value] || value || 'New York City';
  };

  return (
    <div className="min-h-screen py-12">
      <div className="container-custom max-w-5xl mx-auto">
        {/* Back Link */}
        <Link 
          to="/events" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-gold mb-8"
          data-testid="back-to-events"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Events
        </Link>

        {/* Event Header */}
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="badge-category">
                {getCategoryLabel(event.category)}
              </span>
              <span className="inline-flex items-center gap-1 px-3 py-1 text-xs uppercase tracking-widest font-semibold bg-secondary text-secondary-foreground">
                <Globe className="w-3 h-3" /> {getRegionLabel(event.region)}
              </span>
              {event.is_featured && (
                <span className="inline-flex items-center gap-1 px-3 py-1 text-xs uppercase tracking-widest font-semibold bg-primary text-primary-foreground">
                  <Crown className="w-3 h-3" /> Featured
                </span>
              )}
              {event.is_paid && (
                <span className="inline-flex items-center gap-1 px-3 py-1 text-xs uppercase tracking-widest font-semibold bg-gold text-primary">
                  <DollarSign className="w-3 h-3" /> ${event.ticket_price}
                </span>
              )}
              {event.is_public === false && (
                <span className="inline-flex items-center gap-1 px-3 py-1 text-xs uppercase tracking-widest font-semibold bg-red-500/20 text-red-400 border border-red-500/30">
                  <Lock className="w-3 h-3" /> Private
                </span>
              )}
              {event.is_invite_only && (
                <span className="inline-flex items-center gap-1 px-3 py-1 text-xs uppercase tracking-widest font-semibold bg-blue-500/20 text-blue-400 border border-blue-500/30">
                  <Ticket className="w-3 h-3" /> Invite Only
                </span>
              )}
              {event.rsvp_enabled === false && !event.is_paid && (
                <span className="inline-flex items-center gap-1 px-3 py-1 text-xs uppercase tracking-widest font-semibold bg-muted text-muted-foreground">
                  <EyeOff className="w-3 h-3" /> RSVP Closed
                </span>
              )}
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl mb-6">{event.title}</h1>
            
            <div className="flex flex-wrap gap-6 text-muted-foreground mb-8">
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gold" />
                {format(parseISO(event.date), 'EEEE, MMMM d, yyyy')}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-gold" />
                {event.start_time} - {event.end_time}
              </span>
            </div>

            <div className="flex flex-wrap gap-6 text-muted-foreground mb-8">
              <span className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-gold" />
                {event.location}
              </span>
              <span className="flex items-center gap-2">
                <Users className="w-5 h-5 text-gold" />
                {event.rsvp_count} Attendee{event.rsvp_count !== 1 ? 's' : ''}
                {event.capacity && ` / ${event.capacity} spots`}
                {event.max_rsvp && !event.capacity && ` (max ${event.max_rsvp} RSVPs)`}
              </span>
            </div>

            {/* Event Image */}
            <div className="mb-8 overflow-hidden">
              <img
                src={event.image_url && !event.image_url.includes('example.com') ? event.image_url : 'https://images.pexels.com/photos/8761648/pexels-photo-8761648.jpeg'}
                alt={event.title}
                className="w-full aspect-video object-cover"
                onError={(e) => { e.target.src = 'https://images.pexels.com/photos/8761648/pexels-photo-8761648.jpeg'; }}
              />
            </div>

            {/* Description */}
            <div className="mb-8">
              <h2 className="text-2xl mb-4">About This Event</h2>
              <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
                {event.description}
              </p>
            </div>

            {/* Address */}
            <div className="border border-border/50 p-6 mb-8">
              <h3 className="text-lg mb-2 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-gold" />
                Location Details
              </h3>
              <p className="text-muted-foreground">{event.address}</p>
            </div>

            {/* Organizer */}
            <div className="border border-border/50 p-6">
              <p className="overline text-gold mb-2">Organized By</p>
              <p className="text-lg">{event.organizer_name}</p>
              <p className="text-muted-foreground text-sm">{event.organizer_email}</p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="border border-border/50 p-6 bg-card sticky top-24">
              <div className="text-center mb-6">
                <p className="overline text-gold mb-2">Event Date</p>
                <p className="text-3xl">{format(parseISO(event.date), 'MMM d')}</p>
                <p className="text-muted-foreground">{format(parseISO(event.date), 'yyyy')}</p>
              </div>

              {event.is_paid ? (
                // Paid Event - Ticket Purchase
                <>
                  <div className="text-center mb-4 p-4 bg-secondary/30">
                    <p className="text-sm text-muted-foreground mb-1">Ticket Price</p>
                    <p className="text-2xl font-semibold">${event.ticket_price}</p>
                  </div>
                  {hasTicket ? (
                    <Button 
                      disabled 
                      className="w-full mb-4 uppercase tracking-widest rounded-sm bg-green-600"
                      data-testid="ticket-purchased-btn"
                    >
                      Ticket Purchased ✓
                    </Button>
                  ) : isAtCapacity ? (
                    <Button 
                      disabled 
                      className="w-full mb-4 uppercase tracking-widest rounded-sm"
                      data-testid="event-full-btn"
                    >
                      Sold Out
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleBuyTicketClick}
                      disabled={ticketLoading}
                      className="w-full mb-4 bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest rounded-sm"
                      data-testid="buy-ticket-btn"
                    >
                      {ticketLoading ? <span className="spinner mr-2" /> : null}
                      Buy Ticket - ${event.ticket_price}
                    </Button>
                  )}
                </>
              ) : (
                // Free Event - RSVP
                <>
                  {event.rsvp_enabled === false ? (
                    <div className="w-full mb-4 p-4 bg-muted rounded-sm text-center">
                      <EyeOff className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">RSVP is not available for this event</p>
                    </div>
                  ) : event.is_invite_only && !invitationCode && !hasRsvp && !isOrganizer ? (
                    // Invite-only event without valid invitation
                    <div className="w-full mb-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-sm text-center">
                      <Ticket className="w-5 h-5 mx-auto mb-2 text-blue-400" />
                      <p className="text-sm text-blue-400 font-medium">Invite Only Event</p>
                      <p className="text-xs text-muted-foreground mt-1">You need an invitation to RSVP</p>
                    </div>
                  ) : event.is_invite_only && invitationCode && invitationValid?.valid && !hasRsvp ? (
                    // Invite-only event with valid invitation
                    <div className="space-y-3 mb-4">
                      <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-sm">
                        <p className="text-sm text-green-400 text-center">
                          ✓ Valid invitation from {invitationValid.invitation?.inviter_name}
                        </p>
                        {invitationValid.invitation?.custom_message && (
                          <p className="text-xs text-muted-foreground mt-2 italic text-center">
                            "{invitationValid.invitation.custom_message}"
                          </p>
                        )}
                      </div>
                      <Button 
                        onClick={handleRsvp}
                        disabled={rsvpLoading}
                        className="w-full bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest rounded-sm"
                        data-testid="rsvp-with-invite-btn"
                      >
                        {rsvpLoading ? <span className="spinner mr-2" /> : null}
                        Accept Invitation & RSVP
                      </Button>
                    </div>
                  ) : event.is_invite_only && invitationCode && !invitationValid?.valid ? (
                    // Invalid invitation
                    <div className="w-full mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-sm text-center">
                      <X className="w-5 h-5 mx-auto mb-2 text-red-400" />
                      <p className="text-sm text-red-400">{invitationValid?.error || 'Invalid invitation'}</p>
                    </div>
                  ) : isAtCapacity && !hasRsvp ? (
                    <Button 
                      disabled 
                      className="w-full mb-4 uppercase tracking-widest rounded-sm"
                      data-testid="event-full-btn"
                    >
                      {event.max_rsvp && event.rsvp_count >= event.max_rsvp ? 'RSVP Limit Reached' : 'Event Full'}
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleRsvp}
                      disabled={rsvpLoading}
                      className={`w-full mb-4 uppercase tracking-widest rounded-sm ${
                        hasRsvp 
                          ? 'bg-secondary text-secondary-foreground hover:bg-secondary/80' 
                          : 'bg-gold text-primary hover:bg-gold/90'
                      }`}
                      data-testid="rsvp-btn"
                    >
                      {rsvpLoading ? (
                        <span className="spinner mr-2" />
                      ) : hasRsvp ? (
                        'Cancel RSVP'
                      ) : (
                        'RSVP Now'
                      )}
                    </Button>
                  )}
                  {event.max_rsvp && (
                    <p className="text-xs text-muted-foreground text-center mb-4">
                      {event.rsvp_count || 0} / {event.max_rsvp} RSVPs
                    </p>
                  )}
                </>
              )}

              {event.registration_link && (
                <a
                  href={event.registration_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 text-gold hover:underline underline-offset-4"
                  data-testid="external-registration-link"
                >
                  External Registration <ExternalLink className="w-4 h-4" />
                </a>
              )}

              {isOrganizer && (
                <div className="mt-8 pt-6 border-t border-border/50">
                  <p className="overline text-muted-foreground mb-4">Organizer Actions</p>
                  <div className="space-y-3">
                    {/* Invite Only - Send Invitations */}
                    {event.is_invite_only && (
                      <>
                        <Button 
                          variant="outline" 
                          className="w-full rounded-sm uppercase tracking-widest text-sm bg-blue-500/10 border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white"
                          onClick={() => {
                            setInviteDialogOpen(true);
                            fetchInvitations();
                          }}
                          data-testid="send-invitations-btn"
                        >
                          <Mail className="w-4 h-4 mr-2" /> Send Invitations
                        </Button>
                        <Button 
                          variant="outline" 
                          className="w-full rounded-sm uppercase tracking-widest text-sm"
                          onClick={() => {
                            setShowInvitations(!showInvitations);
                            if (!showInvitations) fetchInvitations();
                          }}
                          data-testid="manage-invitations-btn"
                        >
                          <UserPlus className="w-4 h-4 mr-2" /> 
                          {showInvitations ? 'Hide' : 'View'} Invitations ({invitations.length})
                        </Button>
                      </>
                    )}
                    <Button 
                      variant="outline" 
                      className="w-full rounded-sm uppercase tracking-widest text-sm bg-gold/10 border-gold text-gold hover:bg-gold hover:text-primary"
                      onClick={() => navigate(`/events/${event.id}/check-in`)}
                      data-testid="check-in-attendees-btn"
                    >
                      <QrCode className="w-4 h-4 mr-2" /> Check In Attendees
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full rounded-sm uppercase tracking-widest text-sm"
                      onClick={() => navigate(`/edit-event/${event.id}`)}
                      data-testid="edit-event-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" /> Edit Event
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full rounded-sm uppercase tracking-widest text-sm"
                      onClick={() => navigate(`/dashboard?event=${event.id}`)}
                      data-testid="view-attendees-btn"
                    >
                      <Users className="w-4 h-4 mr-2" /> View Attendees
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full rounded-sm uppercase tracking-widest text-sm text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => setDeleteDialogOpen(true)}
                      data-testid="delete-event-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Delete Event
                    </Button>
                  </div>

                  {/* Invitation List */}
                  {event.is_invite_only && showInvitations && (
                    <div className="mt-4 pt-4 border-t border-border/50">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium">Invitation Status</p>
                        {invitations.filter(i => i.status === 'pending').length > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {invitations.filter(i => i.status === 'pending').length} pending
                          </span>
                        )}
                      </div>

                      {/* Bulk Actions - Premium Only */}
                      {isPremiumMember() && invitations.filter(i => i.status === 'pending').length > 0 && (
                        <div className="flex gap-2 mb-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-xs h-8 bg-blue-500/10 border-blue-500/50 text-blue-400 hover:bg-blue-500 hover:text-white"
                            onClick={handleBulkResend}
                            data-testid="bulk-resend-btn"
                          >
                            <RefreshCw className="w-3 h-3 mr-1" /> Resend All
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-xs h-8 bg-red-500/10 border-red-500/50 text-red-400 hover:bg-red-500 hover:text-white"
                            onClick={handleBulkRevoke}
                            data-testid="bulk-revoke-btn"
                          >
                            <X className="w-3 h-3 mr-1" /> Revoke All
                          </Button>
                        </div>
                      )}

                      {/* Premium upgrade prompt for non-premium users */}
                      {!isPremiumMember() && invitations.filter(i => i.status === 'pending').length > 1 && (
                        <div className="mb-3 p-2 bg-gold/10 border border-gold/30 rounded text-xs text-center">
                          <Crown className="w-3 h-3 inline mr-1 text-gold" />
                          <span className="text-gold">Upgrade to Premium for bulk actions</span>
                        </div>
                      )}

                      {invitations.length === 0 ? (
                        <p className="text-xs text-muted-foreground">No invitations sent yet</p>
                      ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {invitations.map((inv) => (
                            <div key={inv.id} className="flex items-center justify-between p-2 bg-secondary/50 rounded text-xs">
                              <div className="flex-1 min-w-0">
                                <p className="truncate">{inv.email}</p>
                                <span className={`text-xs ${
                                  inv.status === 'accepted' ? 'text-green-400' : 
                                  inv.status === 'pending' ? 'text-yellow-400' : 'text-red-400'
                                }`}>
                                  {inv.status}
                                </span>
                              </div>
                              {inv.status === 'pending' && (
                                <div className="flex gap-1 ml-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-2"
                                    onClick={() => handleResendInvitation(inv.id)}
                                  >
                                    <RefreshCw className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-2 text-red-400"
                                    onClick={() => handleRevokeInvitation(inv.id)}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Event Photo Gallery */}
        <div className="mt-12 border-t border-border/50 pt-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl flex items-center gap-2">
              <Image className="w-6 h-6 text-gold" />
              Event Photos
            </h2>
            {isOrganizer && (
              <div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  data-testid="photo-upload-input"
                />
                <Button
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="bg-gold text-primary hover:bg-gold/90"
                  data-testid="upload-photo-btn"
                >
                  {uploadingPhoto ? (
                    <span className="spinner mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Upload Photo
                </Button>
              </div>
            )}
          </div>

          {photosLoading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : photos.length === 0 ? (
            <div className="text-center py-12 border border-border/50 bg-card">
              <Camera className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">
                {isOrganizer 
                  ? 'No photos yet. Upload photos from your event!'
                  : 'No photos have been shared yet.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <div
                  key={photo.id}
                  className="relative aspect-square overflow-hidden rounded-lg cursor-pointer group"
                  onClick={() => setSelectedPhoto(photo)}
                  data-testid={`photo-${photo.id}`}
                >
                  <img
                    src={`data:${photo.content_type};base64,${photo.image_data}`}
                    alt={photo.caption || 'Event photo'}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Photo Viewer Modal */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{selectedPhoto?.caption || 'Event Photo'}</DialogTitle>
          </DialogHeader>
          {selectedPhoto && (
            <div className="space-y-4">
              <img
                src={`data:${selectedPhoto.content_type};base64,${selectedPhoto.image_data}`}
                alt={selectedPhoto.caption || 'Event photo'}
                className="w-full max-h-[70vh] object-contain rounded-lg"
              />
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>Uploaded by {selectedPhoto.uploaded_by_name}</span>
                {isOrganizer && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeletePhoto(selectedPhoto.id)}
                    data-testid="delete-photo-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Event</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this event? This action cannot be undone and all RSVPs will be removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} data-testid="cancel-delete-btn">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Method Selection Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Choose Payment Method</DialogTitle>
            <DialogDescription>
              Select how you'd like to pay for your ticket
            </DialogDescription>
          </DialogHeader>
          
          {event && (
            <div className="space-y-4 py-4">
              {/* Price Summary */}
              <div className="p-4 bg-secondary/50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Ticket Price</span>
                  <span className="text-xl font-semibold">${event.ticket_price}</span>
                </div>
              </div>

              {/* Payment Options */}
              <div className="space-y-3">
                {/* Stripe (Credit Card) */}
                <button
                  onClick={() => handleBuyTicket('stripe')}
                  disabled={ticketLoading}
                  className="w-full flex items-center gap-4 p-4 border border-border/50 rounded-lg hover:border-gold/50 transition-colors text-left"
                  data-testid="pay-stripe-btn"
                >
                  <div className="w-12 h-12 bg-[#635BFF] rounded-lg flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">Credit/Debit Card</p>
                    <p className="text-sm text-muted-foreground">Visa, Mastercard, Amex • Apple Pay • Google Pay</p>
                  </div>
                </button>

                {/* PayPal */}
                {paymentMethods.find(m => m.id === 'paypal')?.available && (
                  <button
                    onClick={() => handleBuyTicket('paypal')}
                    disabled={ticketLoading}
                    className="w-full flex items-center gap-4 p-4 border border-border/50 rounded-lg hover:border-gold/50 transition-colors text-left"
                    data-testid="pay-paypal-btn"
                  >
                    <div className="w-12 h-12 bg-[#003087] rounded-lg flex items-center justify-center">
                      <Wallet className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">PayPal</p>
                      <p className="text-sm text-muted-foreground">Pay with PayPal • Venmo available</p>
                    </div>
                  </button>
                )}
              </div>

              {/* Security Note */}
              <p className="text-xs text-muted-foreground text-center">
                Secure payment processing. Your payment info is never stored on our servers.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Invitations Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={(open) => {
        setInviteDialogOpen(open);
        if (!open) {
          setInviteTab('manual');
          setCsvFile(null);
          setInviteEmails('');
          setInviteMessage('');
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Invitations</DialogTitle>
            <DialogDescription>
              Send private invitations via email. Each person will receive a unique link to RSVP.
            </DialogDescription>
          </DialogHeader>
          
          {/* Tab Switcher */}
          <div className="flex border-b border-border mb-4">
            <button
              onClick={() => setInviteTab('manual')}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                inviteTab === 'manual' 
                  ? 'border-b-2 border-blue-500 text-blue-500' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Manual Entry
            </button>
            <button
              onClick={() => setInviteTab('csv')}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                inviteTab === 'csv' 
                  ? 'border-b-2 border-blue-500 text-blue-500' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              CSV Import
            </button>
          </div>

          {inviteTab === 'manual' ? (
            /* Manual Entry Tab */
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Email Addresses</label>
                <Textarea
                  value={inviteEmails}
                  onChange={(e) => setInviteEmails(e.target.value)}
                  placeholder="Enter emails separated by commas or new lines:&#10;john@example.com&#10;jane@example.com"
                  rows={4}
                  className="resize-none"
                  data-testid="invite-emails-input"
                />
                <p className="text-xs text-muted-foreground">
                  Separate multiple emails with commas or new lines
                </p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Personal Message (Optional)</label>
                <Textarea
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  placeholder="Add a personal note to your invitation..."
                  rows={3}
                  className="resize-none"
                  data-testid="invite-message-input"
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSendInvitations}
                  disabled={sendingInvites || !inviteEmails.trim()}
                  className="bg-blue-500 hover:bg-blue-600"
                  data-testid="send-invites-btn"
                >
                  {sendingInvites ? (
                    <span className="spinner mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Invitations
                </Button>
              </DialogFooter>
            </div>
          ) : (
            /* CSV Import Tab */
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Upload CSV File</label>
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCsvFileChange}
                    ref={csvInputRef}
                    className="hidden"
                    id="csv-upload"
                    data-testid="csv-file-input"
                  />
                  {csvFile ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-2 text-green-500">
                        <Upload className="w-5 h-5" />
                        <span className="font-medium">{csvFile.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setCsvFile(null);
                          if (csvInputRef.current) csvInputRef.current.value = '';
                        }}
                      >
                        <X className="w-4 h-4 mr-1" /> Remove
                      </Button>
                    </div>
                  ) : (
                    <label htmlFor="csv-upload" className="cursor-pointer">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        CSV file with email addresses
                      </p>
                    </label>
                  )}
                </div>
                <button
                  onClick={downloadCsvTemplate}
                  className="text-xs text-blue-500 hover:underline"
                >
                  Download CSV template
                </button>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Personal Message (Optional)</label>
                <Textarea
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  placeholder="Add a personal note to all invitations..."
                  rows={2}
                  className="resize-none"
                />
              </div>

              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  <strong>CSV Format:</strong> Your file should have an "email" column, or just one email per line. 
                  We'll automatically detect and extract valid email addresses.
                </p>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCsvUpload}
                  disabled={uploadingCsv || !csvFile}
                  className="bg-blue-500 hover:bg-blue-600"
                  data-testid="upload-csv-btn"
                >
                  {uploadingCsv ? (
                    <span className="spinner mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Import & Send
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventDetail;
